import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { usePermissions } from '@shared/hooks/usePermissions';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getStatusBadge } from '../../hooks/useTopupsColumns';
import type { Topup } from '../../models';

const formatCurrency = (value?: number | null, currency?: string) => {
  if (value === undefined || value === null) return '—';
  return formatCurrencyAmount(value, currency || 'EUR');
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(date);
};

const maskIban = (iban: string) => {
  if (!iban || iban.length < 8) return iban;
  return iban.slice(0, 4) + '...' + iban.slice(-4);
};

export interface ViewTopupModalRef {
  open: (topup: Topup) => void;
  close: () => void;
}

interface ViewTopupModalProps {
  onClose?: () => void;
}

export const ViewTopupModal = forwardRef<ViewTopupModalRef, ViewTopupModalProps>(({ onClose }, ref) => {
  const { t } = useTranslation('topupsModule');
  const { getTeamType } = usePermissions();
  const currency = useCurrency();
  const teamType = getTeamType();
  const is3pl = teamType === 'tenant_3pl';
  const modalRef = useRef<ModalCenterRef>(null);
  const [topup, setTopup] = useState<Topup | null>(null);

  const openWithTopup = useCallback((t: Topup) => {
    setTopup(t);
    setTimeout(() => {
      modalRef.current?.openModal();
    }, 0);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      open: openWithTopup,
      close: () => modalRef.current?.closeModal(),
    }),
    [openWithTopup],
  );

  const handleClose = useCallback(() => {
    setTopup(null);
    onClose?.();
  }, [onClose]);

  if (!topup) return null;

  return (
    <ModalCenter
      ref={modalRef}
      title={`${t('actions.view')} - #${topup.id}`}
      type="default"
      width="lg"
      onCloseModal={handleClose}
    >
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
        {/* Estado */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{t('table.columns.status')}:</span>
          {getStatusBadge(topup.status, t)}
        </div>

        {/* Información principal */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Info
            label={t('table.columns.wallet')}
            value={topup.wallet?.team?.name ? `${topup.wallet.team.name} (#${topup.wallet_id})` : `#${topup.wallet_id}`}
          />
          <Info label={t('table.columns.payment_method')} value={topup.payment_method} />
          <Info label={t('table.columns.declared_amount')} value={formatCurrency(topup.declared_amount, currency)} />
          <Info label={t('table.columns.confirmed_amount')} value={formatCurrency(topup.confirmed_amount, currency)} />
          <Info label={t('table.columns.payment_reference')} value={topup.payment_reference} />
          <Info label={t('table.columns.transfer_date')} value={formatDate(topup.transfer_date)} />
          <Info label={t('table.columns.payer_name')} value={topup.payer_name} />
          <Info label={t('table.columns.payer_iban')} value={maskIban(topup.payer_iban)} />
          {topup.created_at && <Info label={t('modals.view.requestDate')} value={formatDate(topup.created_at)} />}
        </div>

        {/* Usuarios */}
        <div className="border-t pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground mb-3">{t('modals.view.usersInvolved')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Info
              label={t('table.columns.requested_by')}
              value={
                topup.requested_by_user?.name
                  ? `${topup.requested_by_user.name} (#${topup.requested_by_user_id})`
                  : topup.requested_by_user_id
                    ? `#${topup.requested_by_user_id}`
                    : '—'
              }
            />
            {is3pl && (
              <Info
                label={t('table.columns.reviewed_by')}
                value={
                  topup.reviewed_by_user?.name
                    ? `${topup.reviewed_by_user.name} (#${topup.reviewed_by_user_id})`
                    : topup.reviewed_by_user_id
                      ? `#${topup.reviewed_by_user_id}`
                      : '—'
                }
              />
            )}
          </div>
        </div>

        {/* Notas y razones */}
        {(topup.internal_note || topup.rejection_reason) && (
          <div className="border-t pt-4 space-y-3">
            {topup.internal_note && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('modals.create.fields.internal_note')}</p>
                <p className="text-sm font-medium bg-muted/50 p-3 rounded-md">{topup.internal_note}</p>
              </div>
            )}
            {topup.rejection_reason && (
              <div className="space-y-1">
                <p className="text-xs text-destructive">{t('modals.reject.fields.reason')}</p>
                <p className="text-sm font-medium text-destructive bg-destructive/10 p-3 rounded-md">
                  {topup.rejection_reason}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Comprobante */}
        {topup.proof_document_path && (
          <div className="border-t pt-4 space-y-3">
            <h3 className="text-sm font-semibold text-foreground mb-3">{t('modals.view.proofDocument')}</h3>
            <a
              href={topup.proof_document_path}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline"
            >
              {t('modals.view.viewDocument')}
            </a>
          </div>
        )}
      </div>
    </ModalCenter>
  );
});

ViewTopupModal.displayName = 'ViewTopupModal';

const Info = ({ label, value }: { label: string; value: string | ReactNode }) => (
  <div className="space-y-1">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="font-medium">{value}</p>
  </div>
);
